import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../api/client";
import {
  createUser,
  generateUserPassword,
  issueApiKey,
  removePublicKey,
  revokeApiKey,
  rotateSecret,
  setPublicKey,
  setUserActive,
  setUserPassword,
  showSecret,
  updateUser,
  userListPath,
  withdrawSecret,
  type AccessMethod,
  type ApiMethod,
  type ApiModule,
  type ConsoleUser,
  type Scope,
  type UserListResponse,
  type UserPatch,
} from "../api/users";
import type { RoleRow, RolesResponse } from "../api/types";
import { Empty, ErrorState, Loading } from "../components/States";
import { Breadcrumb, Toolbar } from "../components/Toolbar";
import { fmtDateTime } from "../lib/format";

const ROLES_PATH = "/api/orchestrator/roles";

type RoleChoice = Pick<RoleRow, "name" | "label" | "description">;

/**
 * Offered only when the roles table cannot be read, so the picker still holds
 * the two roles every install has instead of nothing at all.
 */
const FALLBACK_ROLES: RoleChoice[] = [
  { name: "member", label: "Member", description: null },
  { name: "admin", label: "Administrator", description: null },
];

/**
 * The roles a picker offers, from the roles table rather than a list fixed
 * here — a fixed list is what left every role added after admin/member
 * unassignable from this page.
 *
 * `current` is always included. A role missing from the list would otherwise
 * render as the first option, and the page would claim a role the account
 * does not hold.
 */
function roleChoices(roles: RoleRow[] | undefined, current?: string): RoleChoice[] {
  const list: RoleChoice[] = roles && roles.length ? roles : FALLBACK_ROLES;
  if (current && !list.some((r) => r.name === current)) {
    return [...list, { name: current, label: current, description: null }];
  }
  return list;
}

/**
 * Used when the server's response predates the module catalogue, so the page
 * still renders. The server is what enforces; this only labels.
 */
const FALLBACK_METHODS: ApiMethod[] = [
  { key: "password", label: "Username and password", default_scope: "all" },
  { key: "client_secret", label: "Client ID and secret", default_scope: ["submit"] },
  { key: "api_key", label: "API key", default_scope: ["submit"] },
  { key: "key_pair", label: "Public and private key", default_scope: ["submit"] },
];

/** What each method is for, in a line, shown on its card. */
const METHOD_HELP: Record<AccessMethod, string> = {
  password:
    "A person signing in. The scope limits the token from /auth/login when it is used from curl or another app; the console itself follows the role.",
  client_secret:
    "A machine exchanges the client ID and secret at /auth/token for a short-lived token.",
  api_key:
    "One static key sent on every request as the X-API-Key header. Simplest to use; treat it like a password.",
  key_pair:
    "The integrator keeps the private key and signs a short JWT to get a token. Only the public key is stored here.",
};

const tenantsText = (tenants: string[]) => tenants.join(", ");
const parseTenants = (text: string) =>
  text
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

function scopeSummary(scope: Scope | undefined, modules: ApiModule[]): string {
  if (scope === undefined) return "—";
  if (scope === "all") return "All APIs";
  if (scope.length === 0) return "No modules";
  return scope.map((k) => modules.find((m) => m.key === k)?.label ?? k).join(", ");
}

const sameScope = (a: Scope | undefined, b: Scope | undefined) =>
  JSON.stringify(a) === JSON.stringify(b);

/** Whether a method is set up on this account. */
function methodActive(u: ConsoleUser, m: AccessMethod): boolean {
  if (m === "password") return u.has_password;
  if (m === "client_secret") return u.has_client_credential;
  if (m === "api_key") return u.has_api_key;
  return u.has_public_key;
}

/* ---------------------------------------------------------- credentials --- */

/** A credential to hand over: a client secret, an API key or a password. */
interface Reveal {
  username: string;
  kind: "secret" | "password" | "api_key";
  value: string;
  /** Shown beside a client secret, since the holder needs the pair. */
  clientId?: string | null;
  /** True when this was just issued and the old value no longer works. */
  fresh: boolean;
}

const REVEAL_TEXT: Record<Reveal["kind"], { label: string; help: string; replaced: string }> = {
  secret: {
    label: "Client secret",
    help: "Give this to the holder along with the client ID. They exchange the pair at /auth/token for a bearer token.",
    replaced: "the previous secret no longer works",
  },
  api_key: {
    label: "API key",
    help: "Give this to the holder. They send it on every request as the X-API-Key header. It cannot be shown again; losing it means generating a new one.",
    replaced: "the previous key no longer works",
  },
  password: {
    label: "Console password",
    help: "Give this to the person. They sign in with their username or email and this password.",
    replaced: "newly set",
  },
};

/**
 * A credential shown in a blocking panel rather than a toast.
 *
 * A toast that disappears on a timer is how a credential gets lost thirty
 * seconds after it is issued. For a password, or a secret that was just
 * rotated, the old value is already invalid — so losing the new one means
 * issuing a third and interrupting the holder again.
 */
function CredentialPanel({
  reveal,
  onDismiss,
}: {
  reveal: Reveal;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The panel renders at the top of the page, but "Generate key" sits far
  // down the account editor. Without this the key appeared off-screen and
  // looked as if nothing had happened -- for a credential shown only once.
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const copy = (what: string, value: string) => {
    // `?.` on writeText too, not only on clipboard: the optional call yields
    // undefined where the API is missing, and `.then` on that throws — which
    // would break the copy button in exactly the insecure-context/older-browser
    // case the guard was added for.
    navigator.clipboard?.writeText(value)?.then(
      () => setCopied(what),
      () => setCopied(null),
    );
  };

  const text = REVEAL_TEXT[reveal.kind];
  const withClientId = reveal.kind === "secret" && reveal.clientId;

  return (
    <div
      ref={panelRef}
      className="card"
      style={{ borderLeft: "3px solid var(--serious, #b45309)" }}
    >
      <div className="card-head bordered">
        <span className="card-title">
          {text.label} for {reveal.username}
        </span>
        {reveal.fresh ? (
          <span className="pill pill-warning">{text.replaced}</span>
        ) : (
          <span className="pill">stored copy</span>
        )}
      </div>

      <div className="card-body">
        <p className="muted" style={{ margin: 0 }}>
          {text.help}
        </p>

        {withClientId ? (
          <>
            <div className="field-label" style={{ marginTop: 14 }}>
              Client ID
            </div>
            <div className="secret-value" style={{ margin: "6px 0 0" }}>
              {reveal.clientId}
            </div>
          </>
        ) : null}

        <div className="field-label" style={{ marginTop: 14 }}>
          {text.label}
        </div>
        <div className="secret-value" style={{ margin: "6px 0 0" }}>
          {reveal.value}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          <button onClick={() => copy("value", reveal.value)}>
            {copied === "value" ? "Copied" : `Copy ${text.label.toLowerCase()}`}
          </button>
          {withClientId ? (
            <button
              onClick={() => copy("both", `${reveal.clientId}\n${reveal.value}`)}
            >
              {copied === "both" ? "Copied" : "Copy both"}
            </button>
          ) : null}
          <button className="btn-primary" onClick={onDismiss}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/** Set a console password, or have the server invent one. */
function PasswordDialog({
  user,
  onDone,
  onError,
  onCancel,
}: {
  user: ConsoleUser;
  onDone: (r: Reveal) => void;
  onError: (m: string) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<"generate" | "type">("generate");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const res =
        mode === "generate"
          ? await generateUserPassword(user.user_id)
          : await setUserPassword(user.user_id, value);
      onDone({
        username: user.username,
        kind: "password",
        // A typed password is not echoed by the server — deliberately — so it
        // is taken from the field here rather than from the response.
        value: res.password ?? value,
        fresh: true,
      });
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head bordered">
        <span className="card-title">Set a password for {user.username}</span>
      </div>
      <div className="card-body">
        <p className="muted" style={{ marginTop: 0 }}>
          This replaces any current password. The existing one cannot be read —
          it is stored as a one-way hash — so setting a new one is how access is
          handed over.
        </p>

        <div className="form-grid">
          {/* A real radio group: a shared `name` is what makes the two mutually
              exclusive to the browser, and what lets arrow keys move between
              them. React's `checked` alone keeps them consistent on screen
              while leaving keyboard and screen-reader behaviour wrong. */}
          <div
            className="field field-wide field-choice"
            role="radiogroup"
            aria-label="How to set the password"
          >
            <label className="field field-check">
              <input
                type="radio"
                name="pw-mode"
                checked={mode === "generate"}
                onChange={() => setMode("generate")}
              />
              <span className="field-label">
                Generate a strong one{" "}
                <span className="field-hint">shown once, recommended</span>
              </span>
            </label>

            <label className="field field-check">
              <input
                type="radio"
                name="pw-mode"
                checked={mode === "type"}
                onChange={() => setMode("type")}
              />
              <span className="field-label">Type one</span>
            </label>
          </div>

          {mode === "type" ? (
            <label className="field field-wide">
              <span className="field-label">
                Password{" "}
                <span className="field-hint">at least 8 characters</span>
              </span>
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoFocus
                autoComplete="off"
              />
            </label>
          ) : null}
        </div>

        <div className="form-actions">
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || (mode === "type" && value.trim().length < 8)}
          >
            {busy
              ? "Saving…"
              : mode === "generate"
                ? "Generate and show"
                : "Set password"}
          </button>
          <button onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- scope --- */

/**
 * "All APIs" or a chosen set of modules, for one sign-in method.
 *
 * A module the account's role cannot use is still offered: the scope only
 * ever narrows, and the server refuses a route the role lacks whatever the
 * scope says. Saying so here saves an administrator wondering why a ticked
 * module still answers 403.
 */
function ScopePicker({
  id,
  value,
  onChange,
  modules,
  disabled,
}: {
  id: string;
  value: Scope;
  onChange: (v: Scope) => void;
  modules: ApiModule[];
  disabled?: boolean;
}) {
  const chosen = value === "all" ? [] : value;
  return (
    <div className="scope-picker">
      <div className="scope-mode" role="radiogroup" aria-label="Which APIs">
        <label>
          <input
            type="radio"
            name={`${id}-mode`}
            checked={value === "all"}
            disabled={disabled}
            onChange={() => onChange("all")}
          />
          All APIs
        </label>
        <label>
          <input
            type="radio"
            name={`${id}-mode`}
            checked={value !== "all"}
            disabled={disabled}
            onChange={() => onChange(chosen)}
          />
          Selected modules
        </label>
      </div>
      {value !== "all" ? (
        <div className="scope-modules">
          {modules.map((m) => (
            <label key={m.key} title={m.description}>
              <input
                type="checkbox"
                checked={chosen.includes(m.key)}
                disabled={disabled}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...chosen, m.key].sort()
                      : chosen.filter((k) => k !== m.key),
                  )
                }
              />
              <span>
                {m.label}
                <span className="muted scope-desc">{m.description}</span>
              </span>
            </label>
          ))}
          {chosen.length === 0 ? (
            <span className="field-hint">
              No modules ticked: this method reaches nothing.
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* --------------------------------------------------------- account editor --- */

/**
 * Everything about one account in one place: details, role, and each way it
 * can sign in with the APIs that way may reach.
 *
 * Two kinds of change, saved two ways. Details, role and scopes are a draft
 * saved together by "Save changes". Issuing, rotating and revoking a
 * credential happens on the click, because each one changes what works right
 * now — a "Save" that also silently invalidated a secret would be a trap.
 */
function AccountEditor({
  user,
  roles,
  modules,
  methods,
  lastAdmin,
  onClose,
  onReveal,
  onSetPassword,
  onChanged,
}: {
  user: ConsoleUser;
  roles: RoleRow[] | undefined;
  modules: ApiModule[];
  methods: ApiMethod[];
  lastAdmin: boolean;
  onClose: () => void;
  onReveal: (r: Reveal) => void;
  onSetPassword: (u: ConsoleUser) => void;
  onChanged: () => void;
}) {
  const [fullName, setFullName] = useState(user.full_name ?? "");
  const [email, setEmail] = useState(user.email);
  const [tenants, setTenants] = useState(tenantsText(user.tenants));
  const [role, setRole] = useState(user.role);
  const [access, setAccess] = useState<Record<AccessMethod, Scope>>(user.api_access);
  const [pem, setPem] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const choices = roleChoices(roles, role);

  const patch: UserPatch = {};
  if (fullName !== (user.full_name ?? "")) patch.full_name = fullName;
  if (email.trim() !== user.email) patch.email = email.trim();
  if (tenantsText(parseTenants(tenants)) !== tenantsText(user.tenants)) {
    patch.tenants = parseTenants(tenants);
  }
  if (role !== user.role) patch.role = role;
  const scopeChanges = Object.fromEntries(
    methods
      .filter((m) => !sameScope(access[m.key], user.api_access?.[m.key]))
      .map((m) => [m.key, access[m.key]]),
  ) as Partial<Record<AccessMethod, Scope>>;
  if (Object.keys(scopeChanges).length) patch.api_access = scopeChanges;
  const dirty = Object.keys(patch).length > 0;

  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (done) setNotice(done);
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    if (
      patch.role === "admin" &&
      !confirm(
        `Make ${user.username} an administrator? They will be able to view, create, ` +
          "deactivate and re-credential every account.",
      )
    ) {
      return;
    }
    act(() => updateUser(user.user_id, patch), "Saved. Takes effect on the account's next request.");
  };

  const confirmThen = (message: string, fn: () => Promise<unknown>, done: string) => {
    if (confirm(message)) act(fn, done);
  };

  return (
    <div className="card account-editor">
      <div className="card-head bordered">
        <span className="card-title">Edit {user.username}</span>
        <button onClick={onClose} disabled={busy}>
          Close
        </button>
      </div>
      <div className="card-body">
        {error ? <div className="notice notice-error">{error}</div> : null}
        {notice ? <div className="notice">{notice}</div> : null}

        <div className="section-title">Details</div>
        <div className="form-grid">
          <label className="field">
            <span className="field-label">
              Username <span className="field-hint">cannot be changed</span>
            </span>
            <input type="text" value={user.username} disabled />
          </label>
          <label className="field">
            <span className="field-label">Full name</span>
            <input
              id="edit-full-name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Email</span>
            <input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Role</span>
            <select
              id="edit-role"
              value={role}
              disabled={lastAdmin}
              title={
                lastAdmin
                  ? "The last administrator cannot be given another role — make someone else an administrator first"
                  : undefined
              }
              onChange={(e) => setRole(e.target.value)}
            >
              {choices.map((r) => (
                <option key={r.name} value={r.name}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field field-wide">
            <span className="field-label">
              Tenants <span className="field-hint">comma separated; * is every tenant</span>
            </span>
            <input
              id="edit-tenants"
              type="text"
              value={tenants}
              onChange={(e) => setTenants(e.target.value)}
            />
          </label>
        </div>

        <div className="section-title" style={{ marginTop: 22 }}>
          Sign-in methods and API access
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          Each method has its own scope. A scope only narrows what the role
          allows; it never grants a page the role cannot see. Account and role
          administration are never reachable with a client secret, API key or
          key pair.
        </p>

        <div className="method-grid">
          {methods.map((m) => {
            const active = methodActive(user, m.key);
            return (
              <section className="method-card" key={m.key} aria-label={m.label}>
                <div className="method-head">
                  <strong>{m.label}</strong>
                  <span className={`pill ${active ? "pill-good" : ""}`}>
                    {active ? "set up" : "not set up"}
                  </span>
                </div>
                <p className="muted method-help">{METHOD_HELP[m.key]}</p>

                <div className="method-detail">
                  {m.key === "password" ? (
                    <div className="controls">
                      <button disabled={busy} onClick={() => onSetPassword(user)}>
                        {active ? "Set a new password" : "Set password"}
                      </button>
                      {active ? (
                        <button
                          disabled={busy}
                          onClick={() =>
                            confirmThen(
                              `Remove ${user.username}'s password? They can no longer sign in to the console.`,
                              () => setUserPassword(user.user_id, null),
                              "Password removed.",
                            )
                          }
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {m.key === "client_secret" ? (
                    <>
                      {user.client_id ? (
                        <div className="mono method-id">Client ID {user.client_id}</div>
                      ) : null}
                      {active && user.client_secret_prefix ? (
                        <div className="muted mono method-id">
                          Secret {user.client_secret_prefix}… · issued{" "}
                          {fmtDateTime(user.client_secret_issued_at)}
                        </div>
                      ) : null}
                      <div className="controls">
                        <button
                          disabled={busy}
                          onClick={() =>
                            act(async () => {
                              const res = await rotateSecret(user.user_id);
                              onReveal({
                                username: user.username,
                                kind: "secret",
                                value: res.client_secret,
                                clientId: res.user.client_id,
                                fresh: true,
                              });
                            })
                          }
                        >
                          {active ? "Rotate secret" : "Issue secret"}
                        </button>
                        {user.client_secret_viewable ? (
                          <button
                            disabled={busy}
                            onClick={() =>
                              act(async () => {
                                const res = await showSecret(user.user_id);
                                onReveal({
                                  username: user.username,
                                  kind: "secret",
                                  value: res.client_secret,
                                  clientId: user.client_id,
                                  fresh: false,
                                });
                              })
                            }
                          >
                            Show secret
                          </button>
                        ) : null}
                        {active ? (
                          <button
                            disabled={busy}
                            onClick={() =>
                              confirmThen(
                                `Withdraw ${user.username}'s client secret? It and every token issued from it stop working now.`,
                                () => withdrawSecret(user.user_id),
                                "Client secret withdrawn.",
                              )
                            }
                          >
                            Withdraw
                          </button>
                        ) : null}
                      </div>
                    </>
                  ) : null}

                  {m.key === "api_key" ? (
                    <>
                      {active ? (
                        <div className="muted mono method-id">
                          {user.api_key_prefix}… · issued {fmtDateTime(user.api_key_issued_at)}
                        </div>
                      ) : null}
                      <div className="controls">
                        <button
                          disabled={busy}
                          onClick={() => {
                            if (
                              active &&
                              !confirm(
                                "Generate a new API key? The current one stops working now.",
                              )
                            ) {
                              return;
                            }
                            act(async () => {
                              const res = await issueApiKey(user.user_id);
                              onReveal({
                                username: user.username,
                                kind: "api_key",
                                value: res.api_key,
                                fresh: true,
                              });
                            });
                          }}
                        >
                          {active ? "Regenerate key" : "Generate key"}
                        </button>
                        {active ? (
                          <button
                            disabled={busy}
                            onClick={() =>
                              confirmThen(
                                `Revoke ${user.username}'s API key? Requests using it are refused from now.`,
                                () => revokeApiKey(user.user_id),
                                "API key revoked.",
                              )
                            }
                          >
                            Revoke
                          </button>
                        ) : null}
                      </div>
                    </>
                  ) : null}

                  {m.key === "key_pair" ? (
                    <>
                      {active ? (
                        <>
                          <div className="mono method-id">Client ID {user.client_id}</div>
                          <div className="muted mono method-id">
                            {user.public_key_fingerprint} · added{" "}
                            {fmtDateTime(user.public_key_added_at)}
                          </div>
                        </>
                      ) : null}
                      <label className="field">
                        <span className="field-label">
                          {active ? "Replace public key" : "Public key"}{" "}
                          <span className="field-hint">PEM, RSA 2048+, EC P-256 or Ed25519</span>
                        </span>
                        <textarea
                          id={`pem-${user.user_id}`}
                          className="pem"
                          rows={4}
                          placeholder={"-----BEGIN PUBLIC KEY-----\n…\n-----END PUBLIC KEY-----"}
                          value={pem}
                          onChange={(e) => setPem(e.target.value)}
                          spellCheck={false}
                        />
                      </label>
                      <div className="controls">
                        <button
                          disabled={busy || !pem.trim()}
                          onClick={() =>
                            act(async () => {
                              await setPublicKey(user.user_id, pem);
                              setPem("");
                            }, active ? "Public key replaced. Tokens from the old key stop working now." : "Public key saved.")
                          }
                        >
                          Save public key
                        </button>
                        {active ? (
                          <button
                            disabled={busy}
                            onClick={() =>
                              confirmThen(
                                `Remove ${user.username}'s public key? Tokens it issued stop working now.`,
                                () => removePublicKey(user.user_id),
                                "Public key removed.",
                              )
                            }
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </div>

                <div className="field-label" style={{ marginTop: 12 }}>
                  APIs this method can use
                </div>
                <ScopePicker
                  id={`${user.user_id}-${m.key}`}
                  value={access[m.key] ?? m.default_scope}
                  onChange={(v) => setAccess((a) => ({ ...a, [m.key]: v }))}
                  modules={modules}
                  disabled={busy}
                />
              </section>
            );
          })}
        </div>

        <div className="form-actions">
          <button className="btn-primary" onClick={save} disabled={busy || !dirty}>
            {busy ? "Saving…" : "Save changes"}
          </button>
          <button onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <span className="muted actions-note">
            {dirty
              ? "Details, role and API scopes are saved together."
              : "Credential buttons above take effect immediately."}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ new account --- */

function CreateForm({
  roles,
  modules,
  methods,
  onCreated,
  onError,
}: {
  roles: RoleRow[] | undefined;
  modules: ApiModule[];
  methods: ApiMethod[];
  onCreated: (reveals: Reveal[]) => void;
  onError: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [tenants, setTenants] = useState("*");
  const [role, setRole] = useState("member");
  const [withSecret, setWithSecret] = useState(true);
  const [withApiKey, setWithApiKey] = useState(false);
  const [pem, setPem] = useState("");
  const defaults = () =>
    Object.fromEntries(methods.map((m) => [m.key, m.default_scope])) as Record<AccessMethod, Scope>;
  const [access, setAccess] = useState<Record<AccessMethod, Scope>>(defaults);

  // The catalogue arrives with the account list; seed the scopes once it does.
  useEffect(() => setAccess(defaults()), [methods]); // eslint-disable-line react-hooks/exhaustive-deps

  const choices = roleChoices(roles, role);
  const chosen = choices.find((r) => r.name === role);

  if (!open) {
    return (
      <div style={{ marginBottom: 20 }}>
        <button className="btn-primary" onClick={() => setOpen(true)}>
          Add account
        </button>
      </div>
    );
  }

  const enabled: Record<AccessMethod, boolean> = {
    password: password.length > 0,
    client_secret: withSecret,
    api_key: withApiKey,
    key_pair: pem.trim().length > 0,
  };
  const anyMethod = Object.values(enabled).some(Boolean);

  const reset = () => {
    setOpen(false);
    setUsername("");
    setEmail("");
    setFullName("");
    setPassword("");
    setTenants("*");
    setRole("member");
    setWithSecret(true);
    setWithApiKey(false);
    setPem("");
    setAccess(defaults());
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await createUser({
        username: username.trim(),
        email: email.trim(),
        password: password || undefined,
        full_name: fullName || undefined,
        tenants: parseTenants(tenants),
        role,
        with_client_secret: withSecret,
        with_api_key: withApiKey,
        public_key: pem.trim() || undefined,
        // Only the methods being set up, so a method added later starts from
        // its default rather than from a choice nobody made.
        api_access: Object.fromEntries(
          methods.filter((m) => enabled[m.key]).map((m) => [m.key, access[m.key]]),
        ),
      });
      const reveals: Reveal[] = [];
      if (res.client_secret) {
        reveals.push({
          username: res.user.username,
          kind: "secret",
          value: res.client_secret,
          clientId: res.user.client_id,
          fresh: true,
        });
      }
      if (res.api_key) {
        reveals.push({ username: res.user.username, kind: "api_key", value: res.api_key, fresh: true });
      }
      onCreated(reveals);
      reset();
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const methodToggle: Record<AccessMethod, JSX.Element> = {
    password: (
      <label className="field">
        <span className="field-label">
          Console password <span className="field-hint">leave blank for none</span>
        </span>
        <input
          id="new-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
      </label>
    ),
    client_secret: (
      <label className="field field-check">
        <input
          id="new-client-secret"
          type="checkbox"
          checked={withSecret}
          onChange={(e) => setWithSecret(e.target.checked)}
        />
        <span className="field-label">Issue a client ID and secret</span>
      </label>
    ),
    api_key: (
      <label className="field field-check">
        <input
          id="new-api-key"
          type="checkbox"
          checked={withApiKey}
          onChange={(e) => setWithApiKey(e.target.checked)}
        />
        <span className="field-label">Generate an API key</span>
      </label>
    ),
    key_pair: (
      <label className="field">
        <span className="field-label">
          Public key <span className="field-hint">optional, PEM</span>
        </span>
        <textarea
          id="new-public-key"
          className="pem"
          rows={3}
          placeholder="-----BEGIN PUBLIC KEY-----"
          value={pem}
          onChange={(e) => setPem(e.target.value)}
          spellCheck={false}
        />
      </label>
    ),
  };

  return (
    <div className="card">
      <div className="card-head bordered">
        <span className="card-title">New account</span>
      </div>

      <div className="card-body">
        <div className="form-grid">
          <label className="field">
            <span className="field-label">Username</span>
            <input
              id="new-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
          </label>

          <label className="field">
            <span className="field-label">Email</span>
            <input
              id="new-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">
              Full name <span className="field-hint">optional</span>
            </span>
            <input
              id="new-full-name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">Role</span>
            <select id="new-role" value={role} onChange={(e) => setRole(e.target.value)}>
              {choices.map((r) => (
                <option key={r.name} value={r.name}>
                  {r.label}
                </option>
              ))}
            </select>
            {chosen?.description ? (
              <span className="muted" style={{ fontSize: 12 }}>
                {chosen.description}
              </span>
            ) : null}
          </label>

          <label className="field field-wide">
            <span className="field-label">
              Tenants{" "}
              <span className="field-hint">comma separated; * is every tenant</span>
            </span>
            <input
              id="new-tenants"
              type="text"
              value={tenants}
              onChange={(e) => setTenants(e.target.value)}
            />
          </label>
        </div>

        <div className="section-title" style={{ marginTop: 22 }}>
          Sign-in methods and API access
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          Choose at least one. Each can reach all APIs or only selected modules,
          and can be changed later with Edit.
        </p>
        <div className="method-grid">
          {methods.map((m) => (
            <section className="method-card" key={m.key} aria-label={m.label}>
              <div className="method-head">
                <strong>{m.label}</strong>
                {enabled[m.key] ? <span className="pill pill-good">will be set up</span> : null}
              </div>
              <p className="muted method-help">{METHOD_HELP[m.key]}</p>
              <div className="form-grid">{methodToggle[m.key]}</div>
              {enabled[m.key] ? (
                <>
                  <div className="field-label" style={{ marginTop: 12 }}>
                    APIs this method can use
                  </div>
                  <ScopePicker
                    id={`new-${m.key}`}
                    value={access[m.key] ?? m.default_scope}
                    onChange={(v) => setAccess((a) => ({ ...a, [m.key]: v }))}
                    modules={modules}
                  />
                </>
              ) : null}
            </section>
          ))}
        </div>

        <div className="form-actions">
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !username.trim() || !email.trim() || !anyMethod}
            title={anyMethod ? undefined : "Choose at least one sign-in method"}
          >
            {busy ? "Creating…" : "Create account"}
          </button>
          <button onClick={reset} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ page --- */

export function Users() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, error, loading, reload } = useApi<UserListResponse>(
    userListPath(includeInactive),
  );
  // Readable by anyone signed in, so it loads for every administrator. A
  // failure leaves the pickers on FALLBACK_ROLES rather than blocking the page.
  const { data: rolesData } = useApi<RolesResponse>(ROLES_PATH);
  const roles = rolesData?.roles;

  const modules = data?.api_modules ?? [];
  const methods = data?.api_methods?.length ? data.api_methods : FALLBACK_METHODS;

  const [reveals, setReveals] = useState<Reveal[]>([]);
  const [pwFor, setPwFor] = useState<ConsoleUser | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // A failed action must not keep complaining about a list that has since been
  // reloaded, so the message is cleared whenever the data changes underneath it.
  useEffect(() => setActionError(null), [data]);

  const run = useCallback(
    async (id: string, fn: () => Promise<unknown>) => {
      setBusyId(id);
      setActionError(null);
      try {
        await fn();
        reload();
      } catch (e: unknown) {
        setActionError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusyId(null);
      }
    },
    [reload],
  );

  const users = data?.users ?? [];
  const editingUser = users.find((u) => u.user_id === editing) ?? null;

  /**
   * True when demoting or deactivating this account would leave nobody able to
   * manage accounts. The server refuses it regardless — this only stops the UI
   * offering a control whose sole outcome is an error.
   */
  const isLastAdmin = (u: ConsoleUser) =>
    u.role === "admin" && u.is_active && (data?.admin_count ?? 0) <= 1;

  const roleLabel = (name: string) =>
    roleChoices(roles, name).find((r) => r.name === name)?.label ?? name;

  return (
    <>
      <Breadcrumb trail={[{ label: "Users" }]} />

      <div className="page-head">
        <div>
          <h1 className="page-title">Console accounts</h1>
          <p className="page-sub">
            Who can sign in to this portal and its APIs, by which methods, and
            how to revoke it.
          </p>
        </div>
        {data ? (
          <span className="pill">
            {data.total} account{data.total === 1 ? "" : "s"} ·{" "}
            {data.admin_count} admin
          </span>
        ) : null}
      </div>

      <Toolbar
        title="Accounts"
        subtitle="An account is revoked by deactivating it; existing tokens stop working on the next request"
        right={
          <>
            <label
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                marginRight: 12,
              }}
            >
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              <span className="muted">Show deactivated</span>
            </label>
            <button onClick={reload}>Refresh</button>
          </>
        }
      />

      {reveals.map((r, i) => (
        <CredentialPanel
          key={`${r.kind}-${r.value}`}
          reveal={r}
          onDismiss={() => setReveals((all) => all.filter((_, j) => j !== i))}
        />
      ))}

      {actionError ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <ErrorState message={actionError} />
        </div>
      ) : null}

      {pwFor ? (
        <PasswordDialog
          user={pwFor}
          onCancel={() => setPwFor(null)}
          onError={setActionError}
          onDone={(r) => {
            setPwFor(null);
            setReveals((all) => [...all, r]);
            reload();
          }}
        />
      ) : null}

      {editingUser ? (
        <AccountEditor
          key={editingUser.user_id}
          user={editingUser}
          roles={roles}
          modules={modules}
          methods={methods}
          lastAdmin={isLastAdmin(editingUser)}
          onClose={() => setEditing(null)}
          onReveal={(r) => setReveals((all) => [...all, r])}
          onSetPassword={(u) => setPwFor(u)}
          onChanged={reload}
        />
      ) : (
        <CreateForm
          roles={roles}
          modules={modules}
          methods={methods}
          onCreated={(issued) => {
            setReveals((all) => [...all, ...issued]);
            reload();
          }}
          onError={setActionError}
        />
      )}

      {loading ? (
        <div className="card">
          <Loading what="accounts" />
        </div>
      ) : error ? (
        <div className="card">
          <ErrorState message={error} onRetry={reload} />
        </div>
      ) : users.length === 0 ? (
        <div className="card">
          <Empty
            title="No accounts yet"
            hint={
              "Everyone signs in through ORCHESTRATOR_UI_USERS in .env. Run " +
              "`manage_users import-env` to turn those into real accounts that can be " +
              "revoked individually."
            }
          />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Role</th>
                  <th>Tenants</th>
                  <th>Sign-in methods and API access</th>
                  <th>Last seen</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf =
                    !!data?.caller_user_id && data.caller_user_id === u.user_id;
                  const busy = busyId === u.user_id;
                  const lastAdmin = isLastAdmin(u);
                  const active = methods.filter((m) => methodActive(u, m.key));

                  return (
                    <tr
                      key={u.user_id}
                      className={editing === u.user_id ? "row-selected" : undefined}
                      style={u.is_active ? undefined : { opacity: 0.55 }}
                    >
                      <td>
                        <div>
                          <strong>{u.username}</strong>
                          {isSelf ? <span className="muted"> · you</span> : null}
                        </div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {u.email}
                          {u.full_name ? ` · ${u.full_name}` : ""}
                        </div>
                      </td>

                      <td>
                        <span className={`pill ${u.role === "admin" ? "pill-warning" : ""}`}>
                          {roleLabel(u.role)}
                        </span>
                      </td>

                      <td className="mono" style={{ fontSize: 12 }}>
                        {u.tenants.join(", ")}
                      </td>

                      <td style={{ fontSize: 12 }}>
                        {active.length === 0 ? (
                          <span className="muted">nothing — cannot sign in</span>
                        ) : (
                          <ul className="method-list">
                            {active.map((m) => (
                              <li key={m.key}>
                                <span>{m.label}</span>
                                <span className="muted">
                                  {scopeSummary(u.api_access?.[m.key], modules)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>

                      <td style={{ fontSize: 12 }}>
                        {u.last_login_at || u.last_token_at ? (
                          fmtDateTime(u.last_login_at ?? u.last_token_at)
                        ) : (
                          <span className="muted">never</span>
                        )}
                      </td>

                      <td>
                        <span
                          className={`pill ${u.is_active ? "pill-good" : "pill-critical"}`}
                        >
                          {u.is_active ? "active" : "revoked"}
                        </span>
                      </td>

                      <td>
                        <div className="controls" style={{ justifyContent: "flex-end" }}>
                          <button
                            disabled={busy}
                            onClick={() => {
                              setActionError(null);
                              setEditing(u.user_id);
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            disabled={busy || (u.is_active && lastAdmin)}
                            title={
                              u.is_active && lastAdmin
                                ? "The last administrator cannot be deactivated"
                                : undefined
                            }
                            onClick={() => {
                              if (
                                u.is_active &&
                                !confirm(
                                  `Deactivate ${u.username}? Their tokens and keys stop working on the ` +
                                    `next request. This can be undone.`,
                                )
                              ) {
                                return;
                              }
                              run(u.user_id, () => setUserActive(u.user_id, !u.is_active));
                            }}
                          >
                            {u.is_active ? "Deactivate" : "Reactivate"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* `data &&` first: while the list is still loading caller_user_id is
          merely unknown, and showing this then would flash the notice at every
          administrator on every visit. */}
      {data && !data.caller_user_id ? (
        <div className="notice" style={{ marginTop: 24 }}>
          You are signed in through{" "}
          <span className="mono">ORCHESTRATOR_UI_USERS</span> in{" "}
          <span className="mono">.env</span>, not through an account in this
          table — which is why you do not appear above. That entry cannot be
          revoked from here and has no identity to attribute an API call to. Run{" "}
          <span className="mono">manage_users import-env</span> to turn it into
          a real account; the same password keeps working.
        </div>
      ) : null}
    </>
  );
}
